import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCheck, FiArrowRight } from '../../utils/icons';
import { Employee, Organization, Location, SalaryComponent } from '../../types';
import { createEmployee, getOrganizations, getAllLocations, getEmployeeDocUploadUrl, uploadFileToS3 } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

const SALARY_COMPONENT_TYPES: SalaryComponent['component'][] = [
  'Basic',
  'DA',
  'HRA',
  'Conveyance',
  'Bonus',
  'Leave Salary',
  'Work Allowance',
  'Washing Allowance',
  'EC Policy',
  'ESIC',
  'PF',
  'Ex Gratia',
  'Statutory Deductions (In-Hand)',
];

interface EmployeeWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 30px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    font-size: 20px;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 30px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const Step = styled.div<{ $active: boolean; $completed: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 60%;
    width: 80%;
    height: 2px;
    background-color: ${({ $completed, theme }) =>
      $completed ? theme.colors.primary : theme.colors.divider};
  }
`;

const StepCircle = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $active, $completed, theme }) =>
    $completed ? theme.colors.primary : $active ? theme.colors.primary : theme.colors.surfaceLight};
  color: ${({ $active, $completed }) => ($active || $completed ? 'white' : '#999')};
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  z-index: 1;
`;

const StepLabel = styled.div<{ $active: boolean }>`
  font-size: 12px;
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  text-align: center;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 30px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};

  .required {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.2s;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceLight};
  }
`;

const FileInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.errorLight}20;
  border-radius: 4px;
`;

const SuccessText = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 13px;
  padding: 10px;
  background: ${({ theme }) => theme.colors.successLight}20;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 30px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  gap: 12px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ $variant = 'secondary', theme }) =>
    $variant === 'primary'
      ? `
          background: ${theme.colors.primary};
          color: white;
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryDark};
          }
        `
      : `
          background: ${theme.colors.surfaceLight};
          color: ${theme.colors.text.primary};
          &:hover:not(:disabled) {
            background: ${theme.colors.divider};
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const AddSalaryButton = styled.button`
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SalaryComponentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  margin-top: 12px;
`;

const ComponentCheckbox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  transition: all 0.2s;

  &:has(input:checked) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}05;
  }

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }

  label {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    user-select: none;
  }
`;

const AmountInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  margin-top: 4px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TotalSalaryDisplay = styled.div`
  margin-top: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.primary}10;
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;

  strong {
    font-weight: 600;
  }
`;

const RemoveSalaryButton = styled.button`
  margin-top: 12px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.error}10;
  }
`;

export const EmployeeWizardComplete: React.FC<EmployeeWizardProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    email: '',
    mobileNumber: '',
    dateOfBirth: '',
    gender: '',
    bloodGroup: '',
    maritalStatus: '',

    // Address Information
    permanentAddress: '',
    currentAddress: '',
    city: '',
    state: '',
    pincode: '',

    // Emergency Contact
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',

    // Employment Details
    orgId: '',
    locationIds: [] as string[],
    employee_id: '',
    designation: '',
    department: '',
    dateOfJoining: '',
    reportingManager: '',
    employment_type: 'Full-time',
    probationPeriod: '',
    workShift: '',
    vendorId: '',
    salaryComponents: [] as SalaryComponent[],

    // Identity & Banking
    aadharNumber: '',
    panNumber: '',
    bankAccountNumber: '',
    bankIfscCode: '',
    bankBranch: '',
    bankName: '',
    uanNumber: '',
    esiNumber: '',

    // Uniform Information
    shirtSize: '',
    trouserSize: '',
    shoeSize: '',

    // Documents
    profilePhoto: null as File | null,
    aadharCardDoc: null as File | null,
    panCardDoc: null as File | null,
    addressProofDoc: null as File | null,
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showSalarySection, setShowSalarySection] = useState(false);
  const [enabledComponents, setEnabledComponents] = useState<Set<SalaryComponent['component']>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const steps = [
    { number: 1, label: 'Basic Info' },
    { number: 2, label: 'Address' },
    { number: 3, label: 'Employment' },
    { number: 4, label: 'Banking' },
    { number: 5, label: 'Documents' },
  ];

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.orgId) {
      const filtered = locations.filter((loc) => loc.orgId === parseInt(formData.orgId));
      setFilteredLocations(filtered);
      setFormData((prev) => ({ ...prev, locationIds: [] }));
    } else {
      setFilteredLocations([]);
    }
  }, [formData.orgId, locations]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgsResponse, locsResponse] = await Promise.all([
        getOrganizations(undefined, { page: 1, limit: 100 }),
        getAllLocations(undefined, { page: 1, limit: 1000 }),
      ]);

      if (orgsResponse.success && orgsResponse.data) {
        setOrganizations(orgsResponse.data);
      }

      if (locsResponse.success && locsResponse.data) {
        setLocations(locsResponse.data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
    setError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      setHasUnsavedChanges(true);
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setFormData((prev) => {
      const isSelected = prev.locationIds.includes(locationId);
      return {
        ...prev,
        locationIds: isSelected ? prev.locationIds.filter((id) => id !== locationId) : [...prev.locationIds, locationId],
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleToggleComponent = (component: SalaryComponent['component']) => {
    const newEnabled = new Set(enabledComponents);

    if (newEnabled.has(component)) {
      // Remove component
      newEnabled.delete(component);
      setFormData((prev) => ({
        ...prev,
        salaryComponents: prev.salaryComponents.filter(c => c.component !== component),
      }));
    } else {
      // Add component
      newEnabled.add(component);
      setFormData((prev) => ({
        ...prev,
        salaryComponents: [...prev.salaryComponents, { component, amount: 0 }],
      }));
    }

    setEnabledComponents(newEnabled);
    setHasUnsavedChanges(true);
  };

  const handleSalaryAmountChange = (component: SalaryComponent['component'], amount: number) => {
    setFormData((prev) => ({
      ...prev,
      salaryComponents: prev.salaryComponents.map(c =>
        c.component === component ? { ...c, amount } : c
      ),
    }));
    setHasUnsavedChanges(true);
  };

  const getSalaryAmount = (component: SalaryComponent['component']): number => {
    const comp = formData.salaryComponents.find(c => c.component === component);
    return comp?.amount || 0;
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          setError('Employee name is required');
          return false;
        }
        if (!formData.orgId) {
          setError('Organization is required');
          return false;
        }
        if (formData.locationIds.length === 0) {
          setError('At least one location is required');
          return false;
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Invalid email format');
          return false;
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload: any = {
        name: formData.name.trim(),
        orgId: parseInt(formData.orgId),
        locationIds: formData.locationIds.map((id) => parseInt(id)),
        employment_type: formData.employment_type,
      };

      // Add all optional fields
      if (formData.email.trim()) payload.email = formData.email.trim();
      if (formData.mobileNumber.trim()) payload.mobile_number = formData.mobileNumber.trim();
      if (formData.dateOfBirth) payload.dateOfBirth = formData.dateOfBirth;
      if (formData.gender) payload.gender = formData.gender;
      if (formData.bloodGroup) payload.bloodGroup = formData.bloodGroup;
      if (formData.maritalStatus) payload.maritalStatus = formData.maritalStatus;

      if (formData.permanentAddress.trim()) payload.permanentAddress = formData.permanentAddress.trim();
      if (formData.currentAddress.trim()) payload.currentAddress = formData.currentAddress.trim();
      if (formData.city.trim()) payload.city = formData.city.trim();
      if (formData.state.trim()) payload.state = formData.state.trim();
      if (formData.pincode.trim()) payload.pincode = formData.pincode.trim();

      if (formData.emergencyContactName.trim()) payload.emergencyContactName = formData.emergencyContactName.trim();
      if (formData.emergencyContactRelation.trim()) payload.emergencyContactRelation = formData.emergencyContactRelation.trim();
      if (formData.emergencyContactPhone.trim()) payload.emergencyContactPhone = formData.emergencyContactPhone.trim();

      if (formData.employee_id.trim()) payload.employee_id = formData.employee_id.trim();
      if (formData.designation.trim()) payload.designation = formData.designation.trim();
      if (formData.department.trim()) payload.department = formData.department.trim();
      if (formData.dateOfJoining) payload.dateOfJoining = formData.dateOfJoining;
      if (formData.reportingManager.trim()) payload.reportingManager = formData.reportingManager.trim();
      if (formData.probationPeriod) payload.probationPeriod = parseInt(formData.probationPeriod);
      if (formData.workShift.trim()) payload.workShift = formData.workShift.trim();
      if (formData.vendorId.trim()) payload.vendor_id = formData.vendorId.trim();
      if (formData.salaryComponents && formData.salaryComponents.length > 0) {
        payload.salaryComponents = formData.salaryComponents;
      }

      if (formData.aadharNumber.trim()) payload.aadharNumber = formData.aadharNumber.trim();
      if (formData.panNumber.trim()) payload.panNumber = formData.panNumber.trim();
      if (formData.bankAccountNumber.trim()) payload.bankAccountNumber = formData.bankAccountNumber.trim();
      if (formData.bankIfscCode.trim()) payload.bankIfscCode = formData.bankIfscCode.trim();
      if (formData.bankBranch.trim()) payload.bankBranch = formData.bankBranch.trim();
      if (formData.bankName.trim()) payload.bankName = formData.bankName.trim();
      if (formData.uanNumber.trim()) payload.uanNumber = formData.uanNumber.trim();
      if (formData.esiNumber.trim()) payload.esiNumber = formData.esiNumber.trim();
      if (formData.shirtSize.trim()) payload.shirtSize = formData.shirtSize.trim();
      if (formData.trouserSize.trim()) payload.trouserSize = formData.trouserSize.trim();
      if (formData.shoeSize.trim()) payload.shoeSize = formData.shoeSize.trim();

      // Upload documents to S3 if any files are selected
      const uploadedDocuments: any = {};
      let hasDocuments = false;

      // Helper function to upload a single document
      const uploadDocument = async (file: File | null, documentType: 'profile_photo' | 'aadhar_card' | 'pan_card' | 'address_proof', fieldName: string) => {
        if (!file || !formData.orgId) return;

        try {
          // Use a temporary employee ID (will be replaced after employee creation)
          const tempEmployeeId = Date.now().toString();

          // Get presigned upload URL
          const uploadUrlResponse = await getEmployeeDocUploadUrl(
            formData.orgId,
            tempEmployeeId,
            documentType,
            file.name,
            file.type,
            file.size
          );

          if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
            throw new Error(`Failed to get upload URL for ${fieldName}`);
          }

          // Handle nested response data (API wraps response in extra data layer)
          const uploadData = (uploadUrlResponse.data as any).data || uploadUrlResponse.data;

          // Upload file to S3
          const uploadResult = await uploadFileToS3(
            uploadData.uploadUrl,
            uploadData.formData,
            file
          );

          if (!uploadResult.success) {
            throw new Error(`Failed to upload ${fieldName}`);
          }

          // Store document metadata
          uploadedDocuments[fieldName] = {
            fileName: file.name,
            s3Key: uploadData.s3Key,
            fileType: file.type,
            fileSize: file.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.email || 'admin'
          };

          hasDocuments = true;
        } catch (error) {
          console.error(`Error uploading ${fieldName}:`, error);
          throw new Error(`Failed to upload ${fieldName}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      };

      // Upload all selected documents
      try {
        await Promise.all([
          uploadDocument(formData.profilePhoto, 'profile_photo', 'profilePhoto'),
          uploadDocument(formData.aadharCardDoc, 'aadhar_card', 'aadharCardDoc'),
          uploadDocument(formData.panCardDoc, 'pan_card', 'panCardDoc'),
          uploadDocument(formData.addressProofDoc, 'address_proof', 'addressProofDoc')
        ]);

        // Add documents to payload if any were uploaded
        if (hasDocuments) {
          payload.documents = uploadedDocuments;
        }
      } catch (uploadError) {
        // If document upload fails, show error and stop
        setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload documents');
        setSubmitting(false);
        return;
      }

      const response = await createEmployee(payload);

      console.log('Create employee response:', response);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          resetForm();
        }, 1500);
      } else {
        const errorMsg = response.error || 'Failed to create employee';
        console.error('Employee creation failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      name: '',
      email: '',
      mobileNumber: '',
      dateOfBirth: '',
      gender: '',
      bloodGroup: '',
      maritalStatus: '',
      permanentAddress: '',
      currentAddress: '',
      city: '',
      state: '',
      pincode: '',
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyContactPhone: '',
      orgId: '',
      locationIds: [],
      employee_id: '',
      designation: '',
      department: '',
      dateOfJoining: '',
      reportingManager: '',
      employment_type: 'Full-time',
      probationPeriod: '',
      workShift: '',
      vendorId: '',
      salaryComponents: [] as SalaryComponent[],
      aadharNumber: '',
      panNumber: '',
      bankAccountNumber: '',
      bankIfscCode: '',
      bankBranch: '',
      bankName: '',
      uanNumber: '',
      esiNumber: '',
      shirtSize: '',
      trouserSize: '',
      shoeSize: '',
      profilePhoto: null,
      aadharCardDoc: null,
      panCardDoc: null,
      addressProofDoc: null,
    });
    setShowSalarySection(false);
    setEnabledComponents(new Set());
    setError(null);
    setSuccess(false);
    setHasUnsavedChanges(false);
  };

  const handleClose = () => {
    if (!submitting) {
      if (hasUnsavedChanges) {
        const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
        if (!confirmed) return;
      }
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  const renderStepContent = () => {
    if (loading) {
      return <div>Loading...</div>;
    }

    switch (currentStep) {
      case 1:
        return (
          <>
            <SectionTitle>Basic Information</SectionTitle>
            {error && <ErrorText>{error}</ErrorText>}
            {success && <SuccessText>Employee created successfully!</SuccessText>}

            <FormRow>
              <FormGroup>
                <Label>
                  Full Name <span className="required">*</span>
                </Label>
                <Input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
              </FormGroup>

              <FormGroup>
                <Label>
                  Organization <span className="required">*</span>
                </Label>
                <Select name="orgId" value={formData.orgId} onChange={handleInputChange} required>
                  <option value="">Select Organization</option>
                  {organizations.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>

            {formData.orgId && filteredLocations.length > 0 && (
              <FormGroup>
                <Label>
                  Locations <span className="required">*</span>
                </Label>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: `1px solid`, borderRadius: '6px', padding: '8px' }}>
                  {filteredLocations.map((location) => (
                    <CheckboxLabel key={location.locationId}>
                      <input type="checkbox" checked={location.locationId ? formData.locationIds.includes(location.locationId.toString()) : false} onChange={() => location.locationId && handleLocationToggle(location.locationId.toString())} />
                      {location.name || location.locationNickname}
                    </CheckboxLabel>
                  ))}
                </div>
              </FormGroup>
            )}

            <FormRow>
              <FormGroup>
                <Label>Email Address</Label>
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Mobile Number</Label>
                <Input type="tel" name="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} maxLength={10} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Gender</Label>
                <Select name="gender" value={formData.gender} onChange={handleInputChange}>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Blood Group</Label>
                <Select name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange}>
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Marital Status</Label>
                <Select name="maritalStatus" value={formData.maritalStatus} onChange={handleInputChange}>
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </Select>
              </FormGroup>
            </FormRow>
          </>
        );

      case 2:
        return (
          <>
            <SectionTitle>Address & Emergency Contact</SectionTitle>
            {error && <ErrorText>{error}</ErrorText>}

            <FormGroup>
              <Label>Permanent Address</Label>
              <TextArea name="permanentAddress" value={formData.permanentAddress} onChange={handleInputChange} />
            </FormGroup>

            <FormGroup>
              <Label>Current Address</Label>
              <TextArea name="currentAddress" value={formData.currentAddress} onChange={handleInputChange} />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>City</Label>
                <Input type="text" name="city" value={formData.city} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>State</Label>
                <Input type="text" name="state" value={formData.state} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Pincode</Label>
              <Input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} maxLength={6} />
            </FormGroup>

            <SectionTitle style={{ marginTop: '30px' }}>Emergency Contact</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Contact Name</Label>
                <Input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Relation</Label>
                <Input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Contact Phone</Label>
              <Input type="tel" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleInputChange} maxLength={10} />
            </FormGroup>
          </>
        );

      case 3:
        return (
          <>
            <SectionTitle>Employment Details</SectionTitle>
            {error && <ErrorText>{error}</ErrorText>}

            <FormRow>
              <FormGroup>
                <Label>Employee ID</Label>
                <Input type="text" name="employee_id" value={formData.employee_id} onChange={handleInputChange} placeholder="Auto-generated if empty" />
              </FormGroup>

              <FormGroup>
                <Label>Designation</Label>
                <Input type="text" name="designation" value={formData.designation} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Department</Label>
                <Input type="text" name="department" value={formData.department} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Date of Joining</Label>
                <Input type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Reporting Manager</Label>
                <Input type="text" name="reportingManager" value={formData.reportingManager} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Employment Type</Label>
                <Select name="employment_type" value={formData.employment_type} onChange={handleInputChange}>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Intern">Intern</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Probation Period (months)</Label>
                <Input type="number" name="probationPeriod" value={formData.probationPeriod} onChange={handleInputChange} min="0" max="24" />
              </FormGroup>

              <FormGroup>
                <Label>Work Shift</Label>
                <Input type="text" name="workShift" value={formData.workShift} onChange={handleInputChange} placeholder="e.g., 9 AM - 6 PM" />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Vendor ID</Label>
              <Input type="text" name="vendorId" value={formData.vendorId} onChange={handleInputChange} placeholder="Optional vendor identifier" />
            </FormGroup>

            <FormGroup style={{ gridColumn: '1 / -1' }}>
              {!showSalarySection ? (
                <AddSalaryButton type="button" onClick={() => setShowSalarySection(true)}>
                  + Add Salary Information
                </AddSalaryButton>
              ) : (
                <>
                  <SectionTitle>Salary Components (Optional)</SectionTitle>

                  <SalaryComponentsGrid>
                    {SALARY_COMPONENT_TYPES.map((component) => (
                      <ComponentCheckbox key={component}>
                        <div>
                          <input
                            type="checkbox"
                            id={`salary-${component}`}
                            checked={enabledComponents.has(component)}
                            onChange={() => handleToggleComponent(component)}
                          />
                          <label htmlFor={`salary-${component}`}>{component}</label>
                        </div>
                        {enabledComponents.has(component) && (
                          <AmountInput
                            type="number"
                            min="0"
                            value={getSalaryAmount(component) || ''}
                            onChange={(e) =>
                              handleSalaryAmountChange(component, Number(e.target.value) || 0)
                            }
                            placeholder="Amount (₹)"
                          />
                        )}
                      </ComponentCheckbox>
                    ))}
                  </SalaryComponentsGrid>

                  {enabledComponents.size > 0 && (
                    <TotalSalaryDisplay>
                      <strong>Total Salary:</strong> ₹{formData.salaryComponents.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                    </TotalSalaryDisplay>
                  )}

                  <RemoveSalaryButton
                    type="button"
                    onClick={() => {
                      setShowSalarySection(false);
                      setEnabledComponents(new Set());
                      setFormData(prev => ({ ...prev, salaryComponents: [] }));
                    }}
                  >
                    Remove Salary Information
                  </RemoveSalaryButton>
                </>
              )}
            </FormGroup>
          </>
        );

      case 4:
        return (
          <>
            <SectionTitle>Identity & Banking Details</SectionTitle>
            {error && <ErrorText>{error}</ErrorText>}

            <FormRow>
              <FormGroup>
                <Label>Aadhar Number</Label>
                <Input type="text" name="aadharNumber" value={formData.aadharNumber} onChange={handleInputChange} maxLength={12} placeholder="12-digit Aadhar number" />
              </FormGroup>

              <FormGroup>
                <Label>PAN Number</Label>
                <Input type="text" name="panNumber" value={formData.panNumber} onChange={handleInputChange} maxLength={10} placeholder="10-character PAN" style={{ textTransform: 'uppercase' }} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>UAN Number</Label>
                <Input type="text" name="uanNumber" value={formData.uanNumber} onChange={handleInputChange} placeholder="Universal Account Number" />
              </FormGroup>

              <FormGroup>
                <Label>ESI Number</Label>
                <Input type="text" name="esiNumber" value={formData.esiNumber} onChange={handleInputChange} placeholder="Employee State Insurance Number" />
              </FormGroup>
            </FormRow>

            <SectionTitle style={{ marginTop: '30px' }}>Uniform Information</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Shirt Size</Label>
                <Input type="text" name="shirtSize" value={formData.shirtSize} onChange={handleInputChange} placeholder="e.g., S, M, L, XL" />
              </FormGroup>

              <FormGroup>
                <Label>Trouser Size</Label>
                <Input type="text" name="trouserSize" value={formData.trouserSize} onChange={handleInputChange} placeholder="e.g., 28, 30, 32, 34" />
              </FormGroup>

              <FormGroup>
                <Label>Shoe Size</Label>
                <Input type="text" name="shoeSize" value={formData.shoeSize} onChange={handleInputChange} placeholder="e.g., 7, 8, 9, 10" />
              </FormGroup>
            </FormRow>

            <SectionTitle style={{ marginTop: '30px' }}>Bank Account Details</SectionTitle>

            <FormRow>
              <FormGroup>
                <Label>Bank Name</Label>
                <Input type="text" name="bankName" value={formData.bankName} onChange={handleInputChange} />
              </FormGroup>

              <FormGroup>
                <Label>Account Number</Label>
                <Input type="text" name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>IFSC Code</Label>
                <Input type="text" name="bankIfscCode" value={formData.bankIfscCode} onChange={handleInputChange} maxLength={11} style={{ textTransform: 'uppercase' }} />
              </FormGroup>

              <FormGroup>
                <Label>Branch Name</Label>
                <Input type="text" name="bankBranch" value={formData.bankBranch} onChange={handleInputChange} />
              </FormGroup>
            </FormRow>
          </>
        );

      case 5:
        return (
          <>
            <SectionTitle>Document Upload</SectionTitle>
            {error && <ErrorText>{error}</ErrorText>}
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              Upload relevant documents for the employee. Supported formats: PDF, JPG, PNG (Max 5MB per file)
            </p>

            <FormGroup>
              <Label>Profile Photo</Label>
              <FileInput type="file" name="profilePhoto" onChange={handleFileChange} accept="image/*" />
            </FormGroup>

            <FormGroup>
              <Label>Aadhar Card</Label>
              <FileInput type="file" name="aadharCardDoc" onChange={handleFileChange} accept=".pdf,image/*" />
            </FormGroup>

            <FormGroup>
              <Label>PAN Card</Label>
              <FileInput type="file" name="panCardDoc" onChange={handleFileChange} accept=".pdf,image/*" />
            </FormGroup>

            <FormGroup>
              <Label>Address Proof</Label>
              <FileInput type="file" name="addressProofDoc" onChange={handleFileChange} accept=".pdf,image/*" />
            </FormGroup>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Employee Onboarding</Title>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </Header>

        <StepIndicator>
          {steps.map((step) => (
            <Step key={step.number} $active={currentStep === step.number} $completed={currentStep > step.number}>
              <StepCircle $active={currentStep === step.number} $completed={currentStep > step.number}>
                {currentStep > step.number ? <FiCheck /> : step.number}
              </StepCircle>
              <StepLabel $active={currentStep === step.number}>{step.label}</StepLabel>
            </Step>
          ))}
        </StepIndicator>

        <Content>{renderStepContent()}</Content>

        <Footer>
          <div>
            {currentStep > 1 && (
              <Button type="button" onClick={handlePrevious} disabled={submitting || success}>
                Previous
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button type="button" onClick={handleClose} disabled={submitting || success}>
              Cancel
            </Button>
            {currentStep < 5 ? (
              <Button type="button" $variant="primary" onClick={handleNext} disabled={loading}>
                Next <FiArrowRight />
              </Button>
            ) : (
              <Button type="button" $variant="primary" onClick={handleSubmit} disabled={submitting || success || loading}>
                {submitting ? 'Creating...' : 'Create Employee'}
              </Button>
            )}
          </div>
        </Footer>
      </Modal>
    </Overlay>
  );
};
